import { useState } from 'react';

import { Box } from '#/components/Box/Box';
import { AutocompleteTextInputField } from '#/components/Form/AutocompleteTextInputField';
import { useFormContext } from '#/components/Form/FormContext';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { Text } from '#/components/Text/Text';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors } from '#/theme/theme';
import { isNotEmpty } from '#/utils/functional/poly';
import { minLengthFn } from '#/utils/string';
import { showModal } from '#/zustand-stores/modalStore';

import { OrgFieldCard } from './OrgFieldCard';

type Props = {
  name: string;
  label?: string;
  description?: string;
  autoComplete: string;
  orgMatch: { id: string; name: string } | null;
  setOrgMatch: (arg0: { id: string; name: string } | null) => void;
};

const orgResourceUrls = {
  en: '/en/orgs/resources',
  es: '/es/orgs/encuentra-talento',
  pt: '/pt/orgs/recursos',
};

const sanitizeTitle = (value: string) =>
  value.toUpperCase().trim().replace(/\s+/g, ' ');

const sanitizedEqual = (s: string, t: string) =>
  sanitizeTitle(s) === sanitizeTitle(t);

export function NewOrgNameInputField({
  name,
  label,
  description,
  autoComplete,
  orgMatch,
  setOrgMatch,
}: Props) {
  const { i18n, locale } = useI18n();

  const { updateValue } = useFormContext();
  const [showCard, setShowCard] = useState(false);

  // oxlint-disable-next-line typescript/no-explicit-any
  const onSuggestionSelected = (_: any, { suggestion }: any) => {
    updateValue('name', suggestion.name);
    setOrgMatch({
      id: suggestion.value.objectID,
      name: suggestion.name,
    });
    setShowCard(true);
  };

  // oxlint-disable-next-line typescript/no-explicit-any
  const onBlur = (e: any, suggestions: any) => {
    const inputValue = e.target.value;

    if (isNotEmpty(suggestions)) {
      // oxlint-disable-next-line typescript/no-explicit-any
      const foundMatch = suggestions.find((s: any) =>
        sanitizedEqual(s.name, inputValue),
      );
      setShowCard(true);

      if (foundMatch) {
        updateValue('name', foundMatch.name);
        setOrgMatch({
          id: foundMatch.value.objectID,
          name: foundMatch.name,
        });
      } else {
        setOrgMatch(null);
      }
    } else if (orgMatch && !sanitizedEqual(orgMatch.name, inputValue)) {
      setOrgMatch(null);
    }
  };

  return (
    <Box mb={30}>
      <div>
        <AutocompleteTextInputField
          name={name}
          label={label}
          description={description}
          placeholder={i18n('Start Typing...')}
          autocompleteType="NEW_ORG_NAME"
          shouldRenderSuggestions={minLengthFn(3)}
          onBlur={onBlur}
          onSuggestionSelected={onSuggestionSelected}
          large
          autoComplete={autoComplete}
        />
      </div>
      {showCard && (
        <Box
          qaId="create-org-name-information"
          overflow="hidden"
          px="4px"
          py="1px"
          bg={colors.backgroundGrey}
          borderRadius="8px"
        >
          <OrgFieldCard
            content={
              orgMatch
                ? {
                    body: (
                      <>
                        {i18n('This organization is already on Idealist.')}{' '}
                        <Text tag="span" pb={3} color="lightContentGrey">
                          <StyledNativeLinkWithRef
                            qaId="become-administrator-link"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              showModal('ADMIN_REQUEST', {
                                objectId: orgMatch.id,
                                objectName: orgMatch.name,
                                redirectTo: orgResourceUrls[locale],
                              });
                            }}
                          >
                            {i18n('Become an administrator')}
                          </StyledNativeLinkWithRef>
                        </Text>
                      </>
                    ),
                  }
                : {
                    body: i18n(
                      'This organization is not yet hosted on Idealist. Create your organization profile to reach the global community of idealists.',
                    ),
                  }
            }
          />
        </Box>
      )}
    </Box>
  );
}
